import baustein from "baustein";
import analytics from "web/script/analytics/analytics";
import browser from "web/script/utils/browser";
import ScrollToTopButton from "web/script/components/scroll-to-top-button";
import style from "web/script/dom/style";
import rothko from "web/script/modules/rothko";
import factory from "web/script/dom/factory";
import template from "templates/modules/product_feed.jinja";
import store from "web/redux/store";
import environment from "web/script/modules/environment";
import { setButtonUrl, setLoading } from "web/redux/ducks/feed-show-previous";
import { setNextPage as showMoreSetNextPage } from "web/redux/ducks/feed-show-more";
import newInfiniteScrollMixin from "./mixins/infinite-scroll";

import "web/script/components/product-card";
import "web/script/components/ta-collection/product-card";
import "web/script/components/feed-pagination";

const EMPTY_FEED = 0;
const THIN_FEED_PRODUCT_LIMIT = 7;
const CRITEO_PRODUCT_ID_LIMIT = 3;
const PRODUCTS_PER_PAGE = environment.get("paginationProductsPerPage");

export let hasProductCardInteraction = false;

export function productFeedLoadMoreContent(url, productFeed = false) {
    // Exposes an endpoint to load more content from a feed url.
    if (!productFeed) {
        productFeed = baustein.getInstanceOf("product-feed");
    }

    if (productFeed) {
        return productFeed.loadMoreContent(url);
    }

    return Promise.resolve(url);
}

export function productFeedLoadPreviousContent(url, productFeed = false) {
    // Exposes an endpoint to load more content from a feed url.
    if (!productFeed) {
        productFeed = baustein.getInstanceOf("product-feed");
    }

    if (productFeed) {
        return productFeed.loadPreviousContent(url);
    }

    return Promise.resolve(url);
}

export default baustein.register("product-feed", {
    template,

    mixins: [newInfiniteScrollMixin],

    getInitialRenderContext() {
        return {
            product_feed: this.options.feedData,
            modifier: this.options.modifier,
            feed_sort_dropdown: this.options.feedSortDropdown,
        };
    },

    setupEvents(add) {
        add("openOverlay", "product-card", this._onOpenProductOverlay);
        add("infinite-scroll-end", this._onInfiniteScrollEnd);
        add("productCardInteraction", "product-card", this._onProductCardInteraction);
    },

    init() {
        this._seenProductIds = new Set();
        this._endOfFeedReached = false;
        this._infiniteScrollEnd = false;
        // flag to indicate if suggested designer filter previews is already called
        this._fetchedSuggestedDesigner = false;

        this._rothkoClient = new rothko.Rothko("modules/product_feed");

        // scroll to product if present in the url
        const urlParams = new URLSearchParams(window.location.search);
        const productId = urlParams.get("productId");
        if (productId) {
            window.addEventListener("DOMContentLoaded", () => {
                this.scrollToProduct(productId);
            });
        }
    },

    onInsert() {
        const scrollToTopButton = new ScrollToTopButton();
        scrollToTopButton.appendTo(this);

        const productCards = this.findComponents("product-card");
        // Save the first slice of product ids to avoid duplicates in the next pages
        productCards.forEach((product) => this._seenProductIds.add(product.options.productId));
        if (productCards.length === EMPTY_FEED) {
            // don't send the end of feed event
            this._endOfFeedReached = true;
            analytics.event("ENGAGEMENT", "FEED_END", "EMPTY_FEED_LANDING", true);
        } else if (
            productCards.length < THIN_FEED_PRODUCT_LIMIT &&
            analytics.getPageViewsCount() === 1
        ) {
            analytics.event(
                "ENGAGEMENT",
                "FEED_END",
                "THIN_FEED_LANDING",
                productCards.length,
                true
            );
        }

        // check if the user is already at the end of the feed, for tracking
        this._onScroll();

        // and add the event handler
        this.setGlobalHandler("scroll", this._onScroll);

        // Track top of page product ids for criteo remarketing
        if (productCards.length > 0) {
            const criteoProductIds = [];

            productCards.slice(0, CRITEO_PRODUCT_ID_LIMIT).forEach((product) => {
                criteoProductIds.push(product.options.productId);
            });

            analytics.tagEvent(analytics.TAG_NETWORKS.CRITEO, "criteo_product_ids", {
                ids: criteoProductIds,
            });
        }

        this.emit("showCsaUnit", { position: "top" });
    },

    onRemove() {
        this.releaseGlobalHandler("scroll", this._onScroll);
    },

    /**
     * This method is called when previous content should be loaded i.e. when a user clicks load previous.
     * @returns {Promise}
     */
    loadPreviousContent(url) {
        if (!url) {
            return Promise.resolve();
        }

        return this._rothkoClient.fetch({ url }).then((productFeedData) => {
            const pagination = this.options.pagination;
            pagination.previous_button_href = productFeedData.pagination.previous_button_href;
            pagination.summary_text = productFeedData.pagination.summary_text;
            const showMoreButton = productFeedData.show_more_button;
            const showPreviousButton = productFeedData.show_previous_button;
            const previousUrl = showPreviousButton ? showPreviousButton.previous_url : "";
            this.updateOptions({ pagination });
            this.renderData(productFeedData, true);
            const productCards = document.querySelectorAll(".product-card");
            const visibleProducts = Math.max(
                productCards.length,
                showMoreButton.visible_product_count
            );

            store.dispatch(setButtonUrl(previousUrl));
            store.dispatch(setLoading(false));

            store.dispatch(
                showMoreSetNextPage({
                    currentPage: showMoreButton.current_page,
                    showMoreNextUrl: pagination.next_button_href,
                    visibleProductCount: visibleProducts,
                    totalProductCount: showMoreButton.total_product_count,
                })
            );

            return pagination ? pagination.previous_button_href : null;
        });
    },

    /**
     * This method is called by the infinite-scroll mixin when more content should be loaded.
     * @returns {Promise}
     */
    loadMoreContent(url) {
        if (!url) {
            return Promise.resolve();
        }

        return this._rothkoClient.fetch({ url }).then((productFeedData) => {
            const pagination = productFeedData.pagination;
            const showMoreButton = productFeedData.show_more_button;
            this.updateOptions({ pagination });
            this.renderData(productFeedData);
            // return the next url for the infinite-scroll mixin to store
            const productCards = document.querySelectorAll(".product-card");
            const visibleProducts = Math.max(
                productCards.length,
                showMoreButton.visible_product_count
            );

            store.dispatch(
                showMoreSetNextPage({
                    currentPage: showMoreButton.current_page,
                    showMoreNextUrl: showMoreButton.show_more_next_url,
                    visibleProductCount: visibleProducts,
                    totalProductCount: showMoreButton.total_product_count,
                })
            );

            if (!this.options.infiniteScrollEnabled) {
                this.scrollToPage(showMoreButton.current_page);
            }

            return pagination ? pagination.next_button_href : null;
        });
    },

    scrollToPage(page) {
        const productCards = document.querySelectorAll(".product-card");
        const nthCard = (page - 1) * PRODUCTS_PER_PAGE;
        if (productCards[nthCard] !== undefined) {
            productCards[nthCard].scrollIntoView();
        }
    },

    scrollToProduct(productId) {
        const productCard = document.querySelector(`#product-${productId}`);
        if (productCard) {
            productCard.scrollIntoView();
        }
    },

    renderData(data, prepend = false) {
        data.feed_items = data.feed_items.filter((item) => {
            if (item.product_card && this._seenProductIds.has(item.product_card.id)) {
                return false;
            }

            this._seenProductIds.add(item.product_card.id);
            return true;
        });

        const renderData = {
            product_feed: data,
            feed_sort_dropdown: this.options.feedSortDropdown,
            uf_upper_funnel_journey: environment.getFeature("uf_upper_funnel_journey"),
            uf_return_to_feeds: environment.getFeature("uf_return_to_feeds"),
        };

        const fragment = document.createDocumentFragment();
        const productFeed = factory.fromString(this.template(renderData));

        let segments = Array.from(productFeed.querySelectorAll(".product-feed__segment"));
        if (!segments.length) {
            return null;
        }

        segments.forEach((segment) => {
            const segmentTitle = segment.getAttribute("segment-title") || "";
            const existing = this.el.querySelector(`[segment-title="${segmentTitle}"]`);

            if (existing) {
                const itemsFragment = document.createDocumentFragment();

                Array.from(segment.querySelector(".product-feed__segment-items").children).forEach(
                    (c) => {
                        itemsFragment.appendChild(c);
                    }
                );

                const productFeed = existing.querySelector(".product-feed__segment-items");
                if (prepend) {
                    productFeed.prepend(itemsFragment);
                } else {
                    productFeed.appendChild(itemsFragment);
                }

                return;
            }

            if (prepend) {
                fragment.prepend(segment);
            } else {
                fragment.appendChild(segment);
            }
        });

        const productFeedContainer = this.el.querySelector(".product-feed");
        if (prepend) {
            productFeedContainer.prepend(fragment);
        } else {
            productFeedContainer.appendChild(fragment);
        }

        // return the next url for the infinite-scroll mixin to store
        return data.pagination.next_button_href;
    },

    _onOpenProductOverlay() {
        hasProductCardInteraction = true;
    },

    _onInfiniteScrollEnd() {
        this._infiniteScrollEnd = true;
    },

    /**
     * Temporary function that monitors when the user gets to the end of the feed. We want to
     * track when this happens. It's called on scroll.
     * @private
     */
    _onScroll() {
        const bottomPos = style.getPosition(this.el).bottom;
        const difference = browser.getWindowHeight() - bottomPos;

        if (this._infiniteScrollEnd && !this._endOfFeedReached && difference > 0) {
            this._endOfFeedReached = true;
            analytics.event("ENGAGEMENT", "FEED_END", "END_OF_FEED_REACHED", true);
            this.emit("showCsaUnit", { position: "end" });
        }
    },

    /**
     * This turns the product feed into "loading" mode. There isn't a way out of it, since
     * when the loading is complete, a new product feed is created instead that has a fresh state.
     */
    startLoading() {
        this.el.classList.add("product-feed--loading");
    },

    _onProductCardInteraction() {
        hasProductCardInteraction = true;
    },
});
